import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import api from '../../services/api';

const {
    FiUsers, FiDollarSign, FiSettings,
    FiShield, FiCreditCard, FiBarChart3, FiGlobe, FiArrowRight,
    FiActivity
} = FiIcons;

const AdminDashboard = () => {
    const [loading, setLoading] = useState(true);
    const [dashboardStats, setDashboardStats] = useState({
        totalUsers: 0,
        activeSubscriptions: 0,
        revenue: 0
    });

    useEffect(() => {
        loadData();
    }, []);

    const loadData = async () => {
        try {
            //   const customers = await api.getCustomers();
            let customers = await api.get('users');
            
            if (customers.status === 200) {
                customers = customers.data.data;

                const total = customers.length;
                const proSubs = customers.filter(c => (c.subscription_tier || '').toLowerCase() === 'pro');
                const teamSubs = customers.filter(c => (c.subscription_tier || '').toLowerCase() === 'team');

                // Define pricing logic
                const PRO_PRICE = 10;
                const TEAM_PRICE = 25;

                const calculatedRevenue = (proSubs.length * PRO_PRICE) + (teamSubs.length * TEAM_PRICE);
                const totalActiveSubs = proSubs.length + teamSubs.length;

                setDashboardStats({
                    totalUsers: total,
                    activeSubscriptions: totalActiveSubs,
                    revenue: calculatedRevenue
                });
            }
        } catch (error) {
            console.error('Failed to load admin stats:', error);
        } finally {
            setLoading(false);
        }
    };

    const stats = [
        {
            title: 'Total Users',
            value: dashboardStats.totalUsers.toLocaleString(),
            change: '+12.5%',
            trend: 'up',
            icon: FiUsers,
            color: 'blue'
        },
        {
            title: 'Monthly Revenue',
            value: `$${dashboardStats.revenue.toLocaleString()}`,
            change: '+8.2%',
            trend: 'up',
            icon: FiDollarSign,
            color: 'green'
        },
        {
            title: 'Active Subscriptions',
            value: dashboardStats.activeSubscriptions.toLocaleString(),
            change: '+15.3%',
            trend: 'up',
            icon: FiCreditCard,
            color: 'purple'
        }
    ];

    const quickActions = [
        {
            title: 'Branding',
            description: 'Customize application branding and appearance',
            icon: FiSettings,
            link: '/admin/branding',
            color: 'blue'
        },
        {
            title: 'Roles & Permissions',
            description: 'Manage user roles and permissions',
            icon: FiUsers,
            link: '/admin/roles',
            color: 'green'
        },
        {
            title: 'Customers',
            description: 'View and manage all customer accounts',
            icon: FiShield,
            link: '/admin/customers',
            color: 'purple'
        },
        {
            title: 'Recent Activity',
            description: 'Monitor all system actions and user events',
            icon: FiActivity,
            link: '/admin/activity',
            color: 'indigo'
        },
        {
            title: 'Usage Reports',
            description: 'Generate and export usage analytics',
            icon: FiBarChart3,
            link: '/admin/reports',
            color: 'red'
        },
        {
            title: 'Payment Settings',
            description: 'Configure payment methods and billing',
            icon: FiCreditCard,
            link: '/admin/payments',
            color: 'orange'
        },
        {
            title: 'Integrations',
            description: 'Manage third-party integrations and APIs',
            icon: FiGlobe,
            link: '/admin/integrations',
            color: 'indigo'
        }
    ];

    if (loading) {
        return (
            <div className="flex items-center justify-center min-h-screen">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gray-50 dark:bg-gray-900 py-8">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                {/* Header */}
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6 }}
                    className="mb-8"
                >
                    <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-2">
                        Admin Dashboard
                    </h1>
                    <p className="text-gray-600 dark:text-gray-300">
                        Manage your Softvative Expense Tracker platform
                    </p>
                </motion.div>

                {/* Stats Grid */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
                    {stats.map((stat, index) => (
                        <motion.div
                            key={stat.title}
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.6, delay: index * 0.1 }}
                            className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6"
                        >
                            <div className="flex items-center justify-between mb-4">
                                <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${stat.color === 'blue' ? 'bg-blue-100 dark:bg-blue-900/20' :
                                        stat.color === 'green' ? 'bg-green-100 dark:bg-green-900/20' :
                                            stat.color === 'purple' ? 'bg-purple-100 dark:bg-purple-900/20' :
                                                'bg-orange-100 dark:bg-orange-900/20'
                                    }`}>
                                    <SafeIcon icon={stat.icon} className={`w-6 h-6 ${stat.color === 'blue' ? 'text-blue-600 dark:text-blue-400' :
                                            stat.color === 'green' ? 'text-green-600 dark:text-green-400' :
                                                stat.color === 'purple' ? 'text-purple-600 dark:text-purple-400' :
                                                    'text-orange-600 dark:text-orange-400'
                                        }`} />
                                </div>
                                <span className="text-sm font-medium text-green-600 dark:text-green-400">
                                    {stat.change}
                                </span>
                            </div>
                            <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-1">
                                {stat.value}
                            </h3>
                            <p className="text-gray-600 dark:text-gray-300 text-sm">
                                {stat.title}
                            </p>
                        </motion.div>
                    ))}
                </div>

                {/* Quick Actions - Concise List View */}
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6, delay: 0.4 }}
                    className="mb-8"
                >
                    <div className="flex items-center justify-between mb-6">
                        <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
                            Quick Actions
                        </h2>
                    </div>
                    <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden">
                        <div className="divide-y divide-gray-100 dark:divide-gray-700">
                            {quickActions.map((action, index) => (
                                <Link
                                    key={action.title}
                                    to={action.link}
                                    className="group flex items-center gap-4 p-4 hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-all duration-200"
                                >
                                    <div className={`w-10 h-10 rounded-lg flex items-center justify-center shrink-0 transition-transform duration-200 group-hover:scale-110 ${action.color === 'blue' ? 'bg-blue-100 dark:bg-blue-900/20' :
                                            action.color === 'green' ? 'bg-green-100 dark:bg-green-900/20' :
                                                action.color === 'purple' ? 'bg-purple-100 dark:bg-purple-900/20' :
                                                    action.color === 'orange' ? 'bg-orange-100 dark:bg-orange-900/20' :
                                                        action.color === 'red' ? 'bg-red-100 dark:bg-red-900/20' :
                                                            'bg-indigo-100 dark:bg-indigo-900/20'
                                        }`}>
                                        <SafeIcon icon={action.icon} className={`w-5 h-5 ${action.color === 'blue' ? 'text-blue-600 dark:text-blue-400' :
                                                action.color === 'green' ? 'text-green-600 dark:text-green-400' :
                                                    action.color === 'purple' ? 'text-purple-600 dark:text-purple-400' :
                                                        action.color === 'orange' ? 'text-orange-600 dark:text-orange-400' :
                                                            action.color === 'red' ? 'text-red-600 dark:text-red-400' :
                                                                'text-indigo-600 dark:text-indigo-400'
                                            }`} />
                                    </div>
                                    <div className="flex-1 min-w-0">
                                        <h3 className="text-sm font-semibold text-gray-900 dark:text-white group-hover:text-primary-600 dark:group-hover:text-primary-400 transition-colors">
                                            {action.title}
                                        </h3>
                                        <p className="text-xs text-gray-500 dark:text-gray-400 truncate">
                                            {action.description}
                                        </p>
                                    </div>
                                    <SafeIcon
                                        icon={FiArrowRight}
                                        className="w-4 h-4 text-gray-400 group-hover:text-primary-600 dark:group-hover:text-primary-400 transition-colors shrink-0"
                                    />
                                </Link>
                            ))}
                        </div>
                    </div>
                </motion.div>
            </div>
        </div>
    );
};

export default AdminDashboard;